import {
  updateUserScopeService,
  connectMicrosoftMailboxService,
  connectGoogleMailboxService,
  disconnectMailIntegrationService,
  getMailIntegrationStatusService,
} from './userService';
import {
  findClerkExternalAccount,
  findClerkExternalAccountById,
  GOOGLE_MAIL_WORKER_REQUIRED_SCOPE,
  MICROSOFT_FULL_DELEGATED_SCOPE_INVENTORY,
  MICROSOFT_MAILBOX_CONNECT_SCOPES,
  MICROSOFT_MAIL_SEND_SCOPE,
  MICROSOFT_CALENDAR_SCOPE,
} from 'clerk-provider-config';

export const CONTACTS_SCOPE = 'https://www.googleapis.com/auth/contacts.readonly';
export const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.events';

const parseApprovedScopes = (approvedScopes) => {
  const scopes = Array.isArray(approvedScopes)
    ? approvedScopes
    : [approvedScopes];

  return new Set(
    scopes
      .flatMap((scope) => String(scope || '').split(/\s+/))
      .filter(Boolean)
  );
};

const normalizeMicrosoftApprovedScopes = (approvedScopes) => {
  const approvedScopeSet = parseApprovedScopes(approvedScopes);
  return MICROSOFT_FULL_DELEGATED_SCOPE_INVENTORY.filter((scope) =>
    approvedScopeSet.has(scope)
  );
};

const emptyMicrosoftMailState = () => ({
  microsoftMailboxGranted: false,
  microsoftMailSendGranted: false,
  approvedScopes: [],
  microsoftAccount: null,
});

const applyMicrosoftMailState = ({
  state,
  setMicrosoftMailboxGranted,
  setMicrosoftMailSendGranted,
}) => {
  setMicrosoftMailboxGranted?.(state.microsoftMailboxGranted);
  setMicrosoftMailSendGranted?.(state.microsoftMailSendGranted);
};

const getMicrosoftMailErrorCode = (error) =>
  error?.response?.data?.code === 'mail_admin_consent_required'
    ? 'mail_admin_consent_required'
    : 'mail_connection_failed';

const finalizeMicrosoftMailbox = async ({
  state,
  mailIntegrationId = null,
  setMicrosoftMailboxGranted,
  setMicrosoftMailSendGranted,
  setMicrosoftMailErrorCode,
}) => {
  if (mailIntegrationId) await connectMicrosoftMailboxService(mailIntegrationId);
  else await connectMicrosoftMailboxService();
  applyMicrosoftMailState({
    state,
    setMicrosoftMailboxGranted,
    setMicrosoftMailSendGranted,
  });
  setMicrosoftMailErrorCode?.(null);
};

const followExternalVerificationRedirect = (reauthorization) => {
  const redirectUrl = reauthorization?.verification?.externalVerificationRedirectURL?.href;
  if (!redirectUrl) return false;

  window.location.href = redirectUrl;
  return true;
};

const findSelectedClerkExternalAccount = (user, provider, providerAccountId = null) => (
  providerAccountId
    ? findClerkExternalAccountById(user, provider, providerAccountId)
    : findClerkExternalAccount(user, provider)
);

export const computeMicrosoftMailState = async (user, providerAccountId = null) => {
  if (!user) return emptyMicrosoftMailState();

  await user.reload();

  const microsoftAccount = findSelectedClerkExternalAccount(
    user,
    'microsoft',
    providerAccountId,
  );
  if (!microsoftAccount) return emptyMicrosoftMailState();

  const approvedScopes = normalizeMicrosoftApprovedScopes(microsoftAccount.approvedScopes);
  const approvedScopeSet = new Set(approvedScopes);

  return {
    microsoftMailboxGranted: MICROSOFT_MAILBOX_CONNECT_SCOPES.every((scope) =>
      approvedScopeSet.has(scope)
    ),
    microsoftMailSendGranted: approvedScopeSet.has(MICROSOFT_MAIL_SEND_SCOPE),
    approvedScopes,
    microsoftAccount,
  };
};

const autoFinalizedMicrosoftMailboxAccountIds = new Set();

export const autoFinalizeMicrosoftMailboxIfGranted = async ({
  state,
  setMicrosoftMailboxGranted,
  setMicrosoftMailSendGranted,
  setMicrosoftMailErrorCode,
  getStatusFn = getMailIntegrationStatusService,
}) => {
  if (!state?.microsoftMailboxGranted || !state?.microsoftAccount) return;

  const accountId = state.microsoftAccount.id ?? state.microsoftAccount.externalId ?? null;
  if (accountId && autoFinalizedMicrosoftMailboxAccountIds.has(accountId)) return;

  try {
    const backendStatus = await getStatusFn('microsoft').catch(() => null);
    if (backendStatus?.status) {
      if (accountId) autoFinalizedMicrosoftMailboxAccountIds.add(accountId);
      return;
    }

    await finalizeMicrosoftMailbox({
      state,
      setMicrosoftMailboxGranted,
      setMicrosoftMailSendGranted,
      setMicrosoftMailErrorCode,
    });
    if (accountId) autoFinalizedMicrosoftMailboxAccountIds.add(accountId);
  } catch (error) {
    setMicrosoftMailErrorCode?.(getMicrosoftMailErrorCode(error));
    console.error('[AuthUtils] Auto-finalize Microsoft mailbox connection failed:', error);
  }
};

export const handleConnectMicrosoftMailbox = async ({
  user,
  providerAccountId = null,
  mailIntegrationId = null,
  setIsAuthorizing,
  setMicrosoftMailboxGranted,
  setMicrosoftMailSendGranted,
  setMicrosoftMailErrorCode,
}) => {
  setIsAuthorizing?.(true);

  try {
    let state = await computeMicrosoftMailState(user, providerAccountId);
    applyMicrosoftMailState({
      state,
      setMicrosoftMailboxGranted,
      setMicrosoftMailSendGranted,
    });

    if (!state.microsoftAccount) {
      setMicrosoftMailErrorCode?.('mail_account_missing');
      return false;
    }

    if (!state.microsoftMailboxGranted) {
      const reauthorization = await state.microsoftAccount.reauthorize({
        redirectUrl: window.location.href,
        additionalScopes: [...MICROSOFT_MAILBOX_CONNECT_SCOPES],
      });

      if (followExternalVerificationRedirect(reauthorization)) return false;

      state = await computeMicrosoftMailState(user, providerAccountId);
      applyMicrosoftMailState({
        state,
        setMicrosoftMailboxGranted,
        setMicrosoftMailSendGranted,
      });
      if (!state.microsoftMailboxGranted) {
        setMicrosoftMailErrorCode?.('mail_scopes_missing');
        return false;
      }
    }

    await finalizeMicrosoftMailbox({
      state,
      mailIntegrationId,
      setMicrosoftMailboxGranted,
      setMicrosoftMailSendGranted,
      setMicrosoftMailErrorCode,
    });
    return true;
  } catch (error) {
    setMicrosoftMailboxGranted?.(false);
    setMicrosoftMailErrorCode?.(getMicrosoftMailErrorCode(error));
    console.error('[AuthUtils] Microsoft mailbox connection error:', error);
    return false;
  } finally {
    setIsAuthorizing?.(false);
  }
};

export const handleConnectMicrosoftMailSend = async ({
  user,
  setIsAuthorizing,
  setMicrosoftMailboxGranted,
  setMicrosoftMailSendGranted,
  setMicrosoftMailErrorCode,
}) => {
  setIsAuthorizing?.(true);

  try {
    let state = await computeMicrosoftMailState(user);
    applyMicrosoftMailState({
      state,
      setMicrosoftMailboxGranted,
      setMicrosoftMailSendGranted,
    });

    if (!state.microsoftAccount) {
      setMicrosoftMailErrorCode?.('mail_account_missing');
      return;
    }

    if (!state.microsoftMailSendGranted) {
      const additionalScopes = MICROSOFT_FULL_DELEGATED_SCOPE_INVENTORY.filter(
        (scope) => state.approvedScopes.includes(scope) || scope === MICROSOFT_MAIL_SEND_SCOPE
      );
      const reauthorization = await state.microsoftAccount.reauthorize({
        redirectUrl: window.location.href,
        additionalScopes,
      });

      if (followExternalVerificationRedirect(reauthorization)) return;

      state = await computeMicrosoftMailState(user);
      applyMicrosoftMailState({
        state,
        setMicrosoftMailboxGranted,
        setMicrosoftMailSendGranted,
      });
      if (!state.microsoftMailSendGranted) {
        setMicrosoftMailErrorCode?.('mail_scopes_missing');
        return;
      }
    }

    if (!state.microsoftMailboxGranted) {
      setMicrosoftMailErrorCode?.('mail_scopes_missing');
      return;
    }

    await finalizeMicrosoftMailbox({
      state,
      setMicrosoftMailboxGranted,
      setMicrosoftMailSendGranted,
      setMicrosoftMailErrorCode,
    });
  } catch (error) {
    setMicrosoftMailSendGranted?.(false);
    setMicrosoftMailErrorCode?.(getMicrosoftMailErrorCode(error));
    console.error('[AuthUtils] Microsoft Mail.Send connection error:', error);
  } finally {
    setIsAuthorizing?.(false);
  }
};

export const computeGrantedFromUser = async (user, microsoftProviderAccountId = null) => {
  if (!user) {
    return {
      contactsGranted: false,
      calendarGranted: false,
      microsoftCalendarGranted: false,
    };
  }

  await user.reload();

  const additionalScopes = (user.publicMetadata?.additionalScopes || []).filter(Boolean);
  const microsoftAccount = findSelectedClerkExternalAccount(
    user,
    'microsoft',
    microsoftProviderAccountId,
  );
  const microsoftApprovedScopes = normalizeMicrosoftApprovedScopes(
    microsoftAccount?.approvedScopes
  );

  const contactsGranted = additionalScopes.includes(CONTACTS_SCOPE);
  const calendarGranted = additionalScopes.includes(CALENDAR_SCOPE);
  const microsoftCalendarGranted =
    additionalScopes.includes(MICROSOFT_CALENDAR_SCOPE)
    && microsoftApprovedScopes.includes(MICROSOFT_CALENDAR_SCOPE);

  return { contactsGranted, calendarGranted, microsoftCalendarGranted };
};

const CALENDAR_CONNECTION_ERROR_CODES = new Set([
  'CALENDAR_AUTH_REQUIRED',
  'CALENDAR_OAUTH_MISSING',
  'CALENDAR_DISABLED',
  'CALENDAR_CONSENT_DECLINED',
  'CALENDAR_TOKEN_EXPIRED',
  'CALENDAR_ADMIN_CONSENT_REQUIRED',
  'CALENDAR_RATE_LIMITED',
  'CALENDAR_PROVIDER_UNAVAILABLE',
  'CALENDAR_INVALID_INPUT',
  'CALENDAR_TIME_ZONE_INVALID',
  'CALENDAR_PROVIDER_FAILED',
]);

const CALENDAR_CLERK_ERROR_CODES = Object.freeze({
  oauth_access_token_expired: 'CALENDAR_TOKEN_EXPIRED',
  oauth_access_token_invalid: 'CALENDAR_TOKEN_EXPIRED',
  oauth_access_token_missing_scopes: 'CALENDAR_OAUTH_MISSING',
  oauth_access_token_scope_insufficient: 'CALENDAR_OAUTH_MISSING',
  oauth_consent_required: 'CALENDAR_CONSENT_DECLINED',
  calendar_admin_consent_required: 'CALENDAR_ADMIN_CONSENT_REQUIRED',
  mail_admin_consent_required: 'CALENDAR_ADMIN_CONSENT_REQUIRED',
});

const CALENDAR_PROVIDER_ERROR_CODES = Object.freeze({
  InvalidAuthenticationToken: 'CALENDAR_TOKEN_EXPIRED',
  ErrorInvalidToken: 'CALENDAR_TOKEN_EXPIRED',
  ErrorAccessDenied: 'CALENDAR_OAUTH_MISSING',
  ErrorInsufficientPermissions: 'CALENDAR_OAUTH_MISSING',
  Authorization_RequestDenied: 'CALENDAR_ADMIN_CONSENT_REQUIRED',
});

export const normalizeMicrosoftCalendarErrorCode = (error) => {
  if (CALENDAR_CONNECTION_ERROR_CODES.has(error?.code)) return error.code;

  const clerkCode = error?.clerkError?.code;
  if (CALENDAR_CLERK_ERROR_CODES[clerkCode]) {
    return CALENDAR_CLERK_ERROR_CODES[clerkCode];
  }

  const responseCode = error?.response?.data?.code;
  if (CALENDAR_CLERK_ERROR_CODES[responseCode]) {
    return CALENDAR_CLERK_ERROR_CODES[responseCode];
  }
  if (CALENDAR_PROVIDER_ERROR_CODES[responseCode]) {
    return CALENDAR_PROVIDER_ERROR_CODES[responseCode];
  }

  const providerCode = error?.providerCode ?? error?.response?.data?.error?.code;
  if (CALENDAR_PROVIDER_ERROR_CODES[providerCode]) {
    return CALENDAR_PROVIDER_ERROR_CODES[providerCode];
  }

  const status = Number.isInteger(error?.status)
    ? error.status
    : error?.response?.status;
  if (status === 429) return 'CALENDAR_RATE_LIMITED';
  if (status === 401) return 'CALENDAR_TOKEN_EXPIRED';
  if (status === 403) return 'CALENDAR_OAUTH_MISSING';
  return 'CALENDAR_PROVIDER_FAILED';
};

const calendarConnectionOutcome = (connected, errorCode = null, redirecting = false) => ({
  connected,
  errorCode,
  redirecting,
});

const microsoftCalendarConnectionsInFlight = new WeakMap();

const connectMicrosoftCalendar = async ({
  user,
  providerAccountId = null,
  setIsAuthorizing,
  setMicrosoftCalendarGranted,
  setMicrosoftCalendarErrorCode,
}) => {
  setIsAuthorizing?.(true);
  setMicrosoftCalendarErrorCode?.(null);
  let microsoftCalendarWasEnabled = false;
  let redirecting = false;

  const clearUnconfirmedCalendarEnablement = async () => {
    if (microsoftCalendarWasEnabled) {
      await updateUserScopeService(MICROSOFT_CALENDAR_SCOPE, 'remove');
      microsoftCalendarWasEnabled = false;
    }
    setMicrosoftCalendarGranted?.(false);
  };

  try {
    microsoftCalendarWasEnabled = Boolean(
      user?.publicMetadata?.additionalScopes?.includes(MICROSOFT_CALENDAR_SCOPE)
    );
    const microsoftAccount = findSelectedClerkExternalAccount(
      user,
      'microsoft',
      providerAccountId,
    );
    if (!microsoftAccount) {
      await clearUnconfirmedCalendarEnablement();
      const errorCode = 'CALENDAR_AUTH_REQUIRED';
      setMicrosoftCalendarErrorCode?.(errorCode);
      return calendarConnectionOutcome(false, errorCode);
    }

    let approvedScopes = normalizeMicrosoftApprovedScopes(microsoftAccount.approvedScopes);

    if (!approvedScopes.includes(MICROSOFT_CALENDAR_SCOPE)) {
      const reauthorization = await microsoftAccount.reauthorize({
        redirectUrl: window.location.href,
        additionalScopes: [MICROSOFT_CALENDAR_SCOPE],
      });

      if (followExternalVerificationRedirect(reauthorization)) {
        redirecting = true;
        await clearUnconfirmedCalendarEnablement();
        return calendarConnectionOutcome(false, null, true);
      }

      await user.reload();
      const refreshedMicrosoftAccount = findSelectedClerkExternalAccount(
        user,
        'microsoft',
        providerAccountId,
      );
      approvedScopes = normalizeMicrosoftApprovedScopes(
        refreshedMicrosoftAccount?.approvedScopes
      );
      if (!approvedScopes.includes(MICROSOFT_CALENDAR_SCOPE)) {
        await clearUnconfirmedCalendarEnablement();
        const errorCode = 'CALENDAR_OAUTH_MISSING';
        setMicrosoftCalendarErrorCode?.(errorCode);
        return calendarConnectionOutcome(false, errorCode);
      }
    }

    if (!microsoftCalendarWasEnabled) {
      await updateUserScopeService(MICROSOFT_CALENDAR_SCOPE, 'add');
      microsoftCalendarWasEnabled = true;
    }

    const { microsoftCalendarGranted } = await computeGrantedFromUser(
      user,
      providerAccountId,
    );
    if (!microsoftCalendarGranted) {
      await clearUnconfirmedCalendarEnablement();
      const errorCode = 'CALENDAR_OAUTH_MISSING';
      setMicrosoftCalendarErrorCode?.(errorCode);
      return calendarConnectionOutcome(false, errorCode);
    }

    setMicrosoftCalendarGranted?.(microsoftCalendarGranted);
    setMicrosoftCalendarErrorCode?.(null);
    return calendarConnectionOutcome(true);
  } catch (error) {
    let errorCode = normalizeMicrosoftCalendarErrorCode(error);
    try {
      await clearUnconfirmedCalendarEnablement();
    } catch {
      errorCode = 'CALENDAR_PROVIDER_FAILED';
      console.error('[AuthUtils] Microsoft Calendar enablement cleanup failed', {
        code: errorCode,
      });
      setMicrosoftCalendarGranted?.(false);
    }
    setMicrosoftCalendarErrorCode?.(errorCode);
    console.error('[AuthUtils] Microsoft Calendar connection failed', { code: errorCode });
    return calendarConnectionOutcome(false, errorCode);
  } finally {
    if (!redirecting) setIsAuthorizing?.(false);
  }
};

export const handleConnectMicrosoftCalendar = (options) => {
  const user = options?.user;
  if (user && typeof user === 'object') {
    const existing = microsoftCalendarConnectionsInFlight.get(user);
    if (existing) return existing;
  }

  const connection = connectMicrosoftCalendar(options ?? {});
  if (user && typeof user === 'object') {
    microsoftCalendarConnectionsInFlight.set(user, connection);
    connection.finally(() => {
      if (microsoftCalendarConnectionsInFlight.get(user) === connection) {
        microsoftCalendarConnectionsInFlight.delete(user);
      }
    });
  }
  return connection;
};

export const handleConnectScope = async ({
  scope,
  user,
  setIsAuthorizing,
  setContactsGranted,
  setCalendarGranted
}) => {
  setIsAuthorizing(true);
     try {
        const googleAccount = findClerkExternalAccount(user, 'google');

        if (!googleAccount) {
          alert("No Google account connected.");
          return;
        }

        const approvedScopes =
          googleAccount.approvedScopes?.split(" ").filter(Boolean) || [];

        const additionalScopes =
          user.publicMetadata?.additionalScopes || [];

        const hasOAuth = approvedScopes.includes(scope);
        const isEnabled = additionalScopes.includes(scope);

        // Start by enabling the feature
        if (!isEnabled) {
          await updateUserScopeService(scope, "add");
        }

        // If the OAuth is missing, start reauth
        if (!hasOAuth) {
          const reauth = await googleAccount.reauthorize({
            redirectUrl: window.location.href,
            additionalScopes: [...approvedScopes, scope],
          });

          if (reauth?.verification?.externalVerificationRedirectURL) {
            window.location.href =
              reauth.verification.externalVerificationRedirectURL.href;
            return;
          }
        }

        // Update UI state
        if (scope === CONTACTS_SCOPE) setContactsGranted(true);
        if (scope === CALENDAR_SCOPE) setCalendarGranted(true);

      } catch (err) {
        console.error("[AuthUtils] handleConnectScope error:", err);
      } finally {
        setIsAuthorizing(false);
      }
    };

export const handleDisconnectScope = async ({
  scope,
  setContactsGranted,
  setCalendarGranted,
  setMicrosoftCalendarGranted,
}) => {
  try {
    const updatedScopes = await updateUserScopeService(scope, "remove");

    const hasCalendar = updatedScopes.includes(CALENDAR_SCOPE);
    const hasContacts = updatedScopes.includes(CONTACTS_SCOPE);
    const hasMicrosoftCalendar = updatedScopes.includes(MICROSOFT_CALENDAR_SCOPE);

    setCalendarGranted?.(hasCalendar);
    setContactsGranted?.(hasContacts);
    setMicrosoftCalendarGranted?.(hasMicrosoftCalendar);

  } catch (err) {
    console.error("Disconnect failed:", err);
  }
};

export const handleConnectGoogleMailbox = async ({
  user,
  providerAccountId = null,
  mailIntegrationId = null,
  setIsAuthorizing,
  setGoogleMailboxConnected,
  setGoogleMailErrorCode,
}) => {
  setIsAuthorizing?.(true);
  let redirecting = false;
  try {
    let googleAccount = findSelectedClerkExternalAccount(
      user,
      'google',
      providerAccountId,
    );
    if (!googleAccount) {
      setGoogleMailboxConnected?.(false);
      setGoogleMailErrorCode?.('mail_account_missing');
      return false;
    }

    const selectedProviderAccountId = googleAccount.externalId;
    if (!parseApprovedScopes(googleAccount.approvedScopes).has(GOOGLE_MAIL_WORKER_REQUIRED_SCOPE)) {
      const reauthorization = await googleAccount.reauthorize({
        redirectUrl: window.location.href,
        additionalScopes: [GOOGLE_MAIL_WORKER_REQUIRED_SCOPE],
      });
      if (followExternalVerificationRedirect(reauthorization)) {
        redirecting = true;
        return false;
      }

      await user.reload();
      googleAccount = findSelectedClerkExternalAccount(
        user,
        'google',
        selectedProviderAccountId,
      );
      if (!parseApprovedScopes(googleAccount?.approvedScopes).has(GOOGLE_MAIL_WORKER_REQUIRED_SCOPE)) {
        setGoogleMailboxConnected?.(false);
        setGoogleMailErrorCode?.('mail_scopes_missing');
        return false;
      }
    }

    if (mailIntegrationId) await connectGoogleMailboxService(mailIntegrationId);
    else await connectGoogleMailboxService();
    setGoogleMailboxConnected?.(true);
    setGoogleMailErrorCode?.(null);
    return true;
  } catch (error) {
    setGoogleMailboxConnected?.(false);
    setGoogleMailErrorCode?.('mail_connection_failed');
    console.error('[AuthUtils] Google mailbox connection error:', error);
    return false;
  } finally {
    if (!redirecting) setIsAuthorizing?.(false);
  }
};

export const runMailFailureRecovery = async ({
  recoveryIntent,
  user,
  mailIntegrationId = null,
  retry,
  connectGoogle = handleConnectGoogleMailbox,
  connectMicrosoft = handleConnectMicrosoftMailbox,
  getStatus = getMailIntegrationStatusService,
}) => {
  if (typeof retry !== 'function') return false;

  const runRetry = async () => (await retry()) !== false;

  if (recoveryIntent === 'retry') {
    return runRetry();
  }

  if (recoveryIntent === 'connect_google') {
    const status = mailIntegrationId
      ? await getStatus('google', mailIntegrationId)
      : null;
    if (mailIntegrationId && !status?.providerAccountId) return false;
    const connected = await connectGoogle({
      user,
      mailIntegrationId,
      providerAccountId: status?.providerAccountId ?? null,
    });
    if (!connected) return false;
    return runRetry();
  }

  if (
    recoveryIntent === 'connect_microsoft'
    || recoveryIntent === 'retry_microsoft_connection'
  ) {
    const status = mailIntegrationId
      ? await getStatus('microsoft', mailIntegrationId)
      : null;
    if (mailIntegrationId && !status?.providerAccountId) return false;
    const connected = await connectMicrosoft({
      user,
      mailIntegrationId,
      providerAccountId: status?.providerAccountId ?? null,
    });
    if (!connected) return false;
    return runRetry();
  }

  return false;
};

export const handleDisconnectMailIntegration = async ({
  provider,
  setIsAuthorizing,
  setMicrosoftMailboxConnected,
  setGoogleMailboxConnected,
  setMicrosoftMailErrorCode,
  setGoogleMailErrorCode,
}) => {
  setIsAuthorizing?.(true);
  try {
    await disconnectMailIntegrationService();
    if (provider === 'microsoft') {
      setMicrosoftMailboxConnected?.(false);
      setMicrosoftMailErrorCode?.(null);
    } else if (provider === 'google') {
      setGoogleMailboxConnected?.(false);
      setGoogleMailErrorCode?.(null);
    }
  } catch (error) {
    const setErrorCode = provider === 'microsoft' ? setMicrosoftMailErrorCode : setGoogleMailErrorCode;
    setErrorCode?.('mail_disconnect_failed');
    console.error('[AuthUtils] Mail integration disconnect error:', error);
  } finally {
    setIsAuthorizing?.(false);
  }
};
